/**
 * Walking enemies (Goomba + Koopa) — split out of the old
 * PlatformerEntities.js monolith (which barrel-re-exports these).
 *
 * `WalkerEnemyBase` is also the movement base for the Mushroom item
 * (ItemEntities.js): horizontal walker, gravity-bound, flips at walls
 * and ledges.
 */

import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { PLATFORMER_PHYSICS, PLATFORMER_ENTITY_DEFS } from './platformerConfig.js';
import { stepBody, classifyEnemyHit } from './PlatformerPhysics.js';
import { spawnSprite } from './entityHelpers.js';

export class WalkerEnemyBase {
    constructor(c, r, board, theme, props = {}, defKey) {
        this._theme = theme;
        this._board = board;
        this._defKey = defKey;
        const cs = board.cellSize;
        const def = PLATFORMER_ENTITY_DEFS[defKey];
        const w = cs * 0.85;
        const h = cs * 0.85;

        this.x = c * cs + (cs - w) / 2;
        this.y = (r + 1) * cs - h;
        this.w = w;
        this.h = h;
        this.dir = props.dir === 'right' ? 1 : -1;
        this.vx = this.dir * PLATFORMER_PHYSICS.enemySpeed;
        this.vy = 0;
        this.alive = true;
        /** Mirror the sprite to face the walk direction. Subclasses
         *  whose art shouldn't mirror (e.g. the labelled power-up tub)
         *  switch this off. */
        this.faceFlip = true;

        this.display = new Container();
        this._sprite = null;
        this._buildVisual(def);
    }

    _buildVisual(def) {
        const sprite = spawnSprite(this._theme, def.slot, this.w, this.h);
        if (sprite) {
            this.display.addChild(sprite);
            this._sprite = sprite;
            return;
        }
        const g = new Graphics();
        g.ellipse(this.w / 2, this.h * 0.45, this.w * 0.45, this.h * 0.45);
        g.fill(def.fallbackColor);
        // Eyes
        g.circle(this.w * 0.35, this.h * 0.40, 3);
        g.circle(this.w * 0.65, this.h * 0.40, 3);
        g.fill(0xffffff);
        g.circle(this.w * 0.35, this.h * 0.42, 1.5);
        g.circle(this.w * 0.65, this.h * 0.42, 1.5);
        g.fill(0x000000);
        // Feet
        g.rect(this.w * 0.20, this.h * 0.85, this.w * 0.20, this.h * 0.15);
        g.rect(this.w * 0.60, this.h * 0.85, this.w * 0.20, this.h * 0.15);
        g.fill(0x111827);
        this.display.addChild(g);
        this._sprite = g;
    }

    update(_controls, board) {
        if (!this.alive) return;

        // Walk in the current direction; flip on wall hit. We also
        // turn around at ledges so a goomba doesn't politely walk
        // off the edge of a platform — that's the original AI rule.
        this.vx = this.dir * PLATFORMER_PHYSICS.enemySpeed;

        const summary = stepBody(this, board);
        if (summary.hitWallLeft || summary.hitWallRight) {
            this.dir *= -1;
            this.vx = this.dir * PLATFORMER_PHYSICS.enemySpeed;
        }
        if (this.onGround) {
            const cs = board.cellSize;
            // Probe the cell BELOW the leading-edge corner. If empty
            // we're about to walk off — flip.
            const probeC = Math.floor((this.dir > 0 ? this.x + this.w + 1 : this.x - 1) / cs);
            const probeR = Math.floor((this.y + this.h + 1) / cs);
            if (!board.isSolidAt(probeC, probeR)) {
                this.dir *= -1;
                this.vx = this.dir * PLATFORMER_PHYSICS.enemySpeed;
            }
        }
        this.display.x = this.x;
        this.display.y = this.y;

        // Face the walk direction. The shipped art looks LEFT, so a
        // rightward walker mirrors. Only sprites can flip in place
        // (centre anchor); the procedural Graphics fallback is
        // symmetric anyway and has no anchor to mirror around.
        if (this.faceFlip && this._sprite?.anchor) {
            const sx = Math.abs(this._sprite.scale.x);
            this._sprite.scale.x = this.dir > 0 ? -sx : sx;
        }
    }

    /** Default reaction: stomp kills it, side-hit damages the player. */
    onCollidePlayer(player) {
        if (!this.alive) return null;
        const verdict = classifyEnemyHit(player, this);
        if (verdict === 'stomp') {
            this.alive = false;
            this.display.visible = false;
            player.stompBounce();
            return 'stomp';
        }
        if (verdict === 'side') {
            return 'damage';
        }
        return null;
    }

    destroy() {
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}

export class GoombaEntity extends WalkerEnemyBase {
    constructor(c, r, board, theme, props = {}) {
        super(c, r, board, theme, props, 'goomba');
    }
}

export class KoopaEntity extends WalkerEnemyBase {
    constructor(c, r, board, theme, props = {}) {
        super(c, r, board, theme, props, 'koopa');
        this.variant = props.variant || 'green';
    }
}
